import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { PanelHeaderSummary } from 'components/StudyDashboard/MultiplexPanels/PanelHeaderSummary';
import { PanelSlideSummary } from 'components/StudyDashboard/MultiplexPanels/PanelSlideSummary';
import { MultiplexPanelView } from 'interfaces/multiplexPanels';
import { every, filter, find, includes, keys, map, reduce, size, sumBy } from 'lodash';
import React, { useState } from 'react';
import { CellSpotDetectionIllustration } from './CellSpotDetectionIllustration';
import {
  buildDefaultPeakDetectionChannelProfile,
  buildDefaultRawDogPeakDetectionChannelProfile,
  buildPeakDetectionSpacingDistanceGroups,
  getPeakDetectionUmppFromSlideMetadata,
} from './helpers';
import { MultiplexPeakDetectionProfile, PanelPeakDetectionConfiguration } from './types';

interface Props {
  panel: MultiplexPanelView;
  configuration: PanelPeakDetectionConfiguration;
  selectedSlideIds: string[];
  onChange: (value: PanelPeakDetectionConfiguration) => void;
  disabled?: boolean;
  normalizationStatus?: React.ReactNode;
  included?: boolean;
  onIncludeChange?: (included: boolean) => void;
  selectionDisabled?: boolean;
  labId?: string;
  customResolutionUmpp?: number | null;
}

const ProfileFields = ({
  profile,
  useRawDog,
  onChange,
  label,
  illustration,
}: {
  profile: MultiplexPeakDetectionProfile;
  useRawDog: boolean;
  onChange: (value: MultiplexPeakDetectionProfile) => void;
  label: string;
  illustration?: React.ReactNode;
}) => {
  const thresholdKey = useRawDog ? 'clipMinFraction' : 'threshold';
  const fields: Array<{
    key: string;
    label: string;
    optional: boolean;
    min: number;
    max?: number;
    step: number | 'any';
  }> = [
    {
      key: thresholdKey,
      label: useRawDog ? 'Lower clipping fraction' : 'Normalized signal threshold',
      optional: false,
      min: 0,
      max: 1,
      step: 'any',
    },
    { key: 'minDistancePx', label: 'Minimum spot spacing (px)', optional: false, min: 1, max: undefined, step: 1 },
    {
      key: 'minRadiusUm',
      label: 'Minimum spot radius (micrometres)',
      optional: true,
      min: 0,
      max: undefined,
      step: 'any',
    },
    {
      key: 'maxRadiusUm',
      label: 'Maximum spot radius (micrometres)',
      optional: true,
      min: 0,
      max: undefined,
      step: 'any',
    },
  ];
  const renderField = (field: (typeof fields)[number]) => {
    const value = (profile as unknown as Record<string, number | null>)[field.key];
    const invalid =
      value != null &&
      (!Number.isFinite(value) ||
        value < field.min ||
        (field.max != null && value > field.max) ||
        (field.key === 'minDistancePx' && !Number.isInteger(value)));
    return (
      <TextField
        key={field.key}
        size="small"
        label={field.label}
        type="number"
        value={value == null || !Number.isFinite(value) ? '' : value}
        inputProps={{
          min: field.min,
          max: field.max,
          step: field.step,
          'aria-label': `${label}: ${field.label}`,
        }}
        error={invalid}
        helperText={
          field.optional
            ? field.key === 'minRadiusUm'
              ? 'Optional. No minimum when empty.'
              : 'Optional. No maximum when empty.'
            : field.key === thresholdKey
            ? useRawDog
              ? 'Sets the background floor before detection. Default: 3% of the upper clipping level.'
              : 'Default 0.06 is the brightness cutoff after normalization.'
            : 'Keeps stronger peaks when spots are too close. Default: 3 working pixels.'
        }
        onChange={(event) =>
          onChange({
            ...profile,
            [field.key]: event.target.value === '' ? (field.optional ? null : NaN) : Number(event.target.value),
          })
        }
      />
    );
  };
  return illustration ? (
    <Stack spacing={2}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(210px, 0.8fr) minmax(0, 1.2fr)',
          gap: 2,
          alignItems: 'start',
        }}
      >
        <Stack data-testid="spot-default-settings" spacing={2}>
          {map(
            filter(fields, (field) => !field.optional),
            renderField
          )}
        </Stack>
        <Box data-testid="spot-setting-visualization" sx={{ minWidth: 0 }}>
          {illustration}
        </Box>
      </Box>
      <Box
        data-testid="spot-radius-settings"
        sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}
      >
        {map(
          filter(fields, (field) => field.optional),
          renderField
        )}
      </Box>
    </Stack>
  ) : (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
      {map(fields, renderField)}
    </Box>
  );
};

export const PanelPeakDetectionEditor = ({
  panel,
  configuration,
  selectedSlideIds,
  onChange,
  disabled,
  normalizationStatus,
  included = true,
  onIncludeChange,
  selectionDisabled,
  labId,
  customResolutionUmpp,
}: Props) => {
  const [expanded, setExpanded] = useState(true);
  const scopedSlides = filter(panel.slides, (slide) => includes(selectedSlideIds, slide.slideId));
  const umppBySlideId = reduce(
    scopedSlides,
    (result, slide) => {
      const umpp = getPeakDetectionUmppFromSlideMetadata(slide);
      if (umpp != null) result[slide.slideId] = umpp;
      return result;
    },
    {} as Record<string, number>
  );
  const invalidCustomResolution =
    customResolutionUmpp != null && (!Number.isFinite(customResolutionUmpp) || customResolutionUmpp <= 0);
  const distanceGroups = invalidCustomResolution
    ? []
    : buildPeakDetectionSpacingDistanceGroups({
        minDistancePx: configuration.defaultProfile.minDistancePx,
        slideIds: map(scopedSlides, 'slideId'),
        umppBySlideId,
        customResolutionUmpp,
      });
  const unknownResolutionCount = Math.max(0, size(scopedSlides) - sumBy(distanceGroups, 'slideCount'));
  const options = filter(
    panel.channels,
    (channel) => Boolean(channel.saved) && channel.saved?.channelModality !== 'dna'
  );
  const isRna = (analysisName: string) => {
    const channel = find(options, (entry) => entry.saved?.analysisName === analysisName);
    return Boolean(
      channel &&
        size(scopedSlides) &&
        every(scopedSlides, (slide) => {
          const index = find(keys(slide.channelMapping), (key) => slide.channelMapping[key] === channel.channelIndex);
          return index != null && includes(slide.rnaChannelIds, index);
        })
    );
  };
  const markerLabel = (analysisName: string) => {
    const channel = find(options, (entry) => entry.saved?.analysisName === analysisName);
    return channel?.saved?.displayName || channel?.saved?.stainTypeId || analysisName;
  };
  const panelName = panel.name || 'Multiplex panel';
  const selector = (value: string[], change: (selected: string[]) => void, label: string, available: string[]) => (
    <Autocomplete
      multiple
      disableCloseOnSelect
      options={available}
      value={value}
      getOptionLabel={markerLabel}
      onChange={(_, selected) => change(selected)}
      disabled={disabled}
      renderTags={(selected, getTagProps) =>
        map(selected, (marker, index) => (
          <Chip
            {...getTagProps({ index })}
            label={`${markerLabel(marker)} (${isRna(marker) ? 'RNA' : 'mIF'})`}
            size="small"
          />
        ))
      }
      renderInput={(params) => <TextField {...params} label={label} />}
    />
  );
  return (
    <Accordion expanded={expanded} onChange={(_, next) => setExpanded(next)} disableGutters>
      <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'action.hover' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-label={panelName} sx={{ flex: 1, minWidth: 0 }}>
          <PanelHeaderSummary
            title={panelName}
            markerNames={map(options, (channel) => markerLabel(channel.saved!.analysisName).trim())}
          >
            <Chip size="small" label={`${size(scopedSlides)} ${size(scopedSlides) === 1 ? 'slide' : 'slides'}`} />
          </PanelHeaderSummary>
        </AccordionSummary>
        {onIncludeChange ? (
          <FormControlLabel
            sx={{ mr: 2, ml: 1 }}
            label="Include"
            control={
              <Switch
                size="small"
                color="secondary"
                checked={included}
                disabled={selectionDisabled}
                inputProps={{ role: 'switch', 'aria-label': `Include ${panelName} in run` }}
                onChange={(_, checked) => onIncludeChange(checked)}
              />
            }
          />
        ) : null}
      </Box>
      <AccordionDetails>
        <Stack spacing={2} component="fieldset" disabled={disabled} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            color="secondary"
            value={configuration.useRawDog ? 'v2' : 'standard'}
            aria-label={`Algorithm for ${panelName}`}
            disabled={disabled}
            sx={{ alignSelf: 'flex-start' }}
            onChange={(_, value: string | null) => {
              if (!value) return;
              const useRawDog = value === 'v2';
              const profile = useRawDog
                ? buildDefaultRawDogPeakDetectionChannelProfile()
                : buildDefaultPeakDetectionChannelProfile();
              onChange({
                ...configuration,
                useRawDog,
                defaultProfile: profile,
                overrides: map(configuration.overrides, (override) => ({ ...override, profile: { ...profile } })),
              });
            }}
          >
            <ToggleButton value="v2">Advanced spot detection</ToggleButton>
            <ToggleButton value="standard">Simple cutoff spot detector</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="body2" color="text.secondary">
            {configuration.useRawDog
              ? 'Finds local peaks and filters background noise. No normalization required.'
              : 'Finds peaks above a brightness cutoff on normalized signals. Requires normalization for every selected marker and slide.'}
          </Typography>
          {selector(
            configuration.channelNames,
            (channelNames) => onChange({ ...configuration, channelNames }),
            `${panelName}: Markers`,
            map(options, (channel) => channel.saved!.analysisName)
          )}
          <PanelSlideSummary panel={{ ...panel, slides: scopedSlides }} labId={labId} />
          {size(filter(configuration.channelNames, (marker) => !isRna(marker))) > 0 ? (
            <Alert severity="warning">
              Non-RNA markers are selected. Spot detection will run on their assigned signals; their modality will not
              change.
            </Alert>
          ) : null}
          {!configuration.useRawDog ? normalizationStatus : null}
          <Typography variant="subtitle2">Panel defaults</Typography>
          <ProfileFields
            profile={configuration.defaultProfile}
            useRawDog={configuration.useRawDog}
            label={`${panelName} defaults`}
            onChange={(defaultProfile) => onChange({ ...configuration, defaultProfile })}
            illustration={
              <CellSpotDetectionIllustration
                profile={configuration.defaultProfile}
                useRawDog={configuration.useRawDog}
                panelName={panelName}
                distanceGroups={distanceGroups}
                unknownResolutionCount={unknownResolutionCount}
              />
            }
          />
          {map(configuration.overrides, (override, overrideIndex) => (
            <Stack spacing={1} key={overrideIndex}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2">Override {overrideIndex + 1}</Typography>
                <Tooltip title="Remove override">
                  <IconButton
                    color="inherit"
                    aria-label={`Remove override ${overrideIndex + 1} from ${panelName}`}
                    onClick={() =>
                      onChange({
                        ...configuration,
                        overrides: filter(configuration.overrides, (_, index) => index !== overrideIndex),
                      })
                    }
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              {selector(
                override.channelNames,
                (channelNames) =>
                  onChange({
                    ...configuration,
                    overrides: map(configuration.overrides, (entry, index) =>
                      index === overrideIndex ? { ...entry, channelNames } : entry
                    ),
                  }),
                `${panelName}: Override ${overrideIndex + 1} markers`,
                configuration.channelNames
              )}
              <ProfileFields
                profile={override.profile}
                useRawDog={configuration.useRawDog}
                label={`${panelName} override ${overrideIndex + 1}`}
                onChange={(profile) =>
                  onChange({
                    ...configuration,
                    overrides: map(configuration.overrides, (entry, index) =>
                      index === overrideIndex ? { ...entry, profile } : entry
                    ),
                  })
                }
              />
              <Typography variant="caption" color="text.secondary">
                Other selected markers keep the panel defaults.
              </Typography>
            </Stack>
          ))}
          <Box>
            <Button
              color="secondary"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() =>
                onChange({
                  ...configuration,
                  overrides: [
                    ...configuration.overrides,
                    { channelNames: [], profile: { ...configuration.defaultProfile } },
                  ],
                })
              }
            >
              Add override
            </Button>
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
